import { StaticFieldSchema } from 'core/utils/generated/formSchemas'
import { isImmutableField } from 'core/utils/immutableFields'
import { flattenObject } from 'core/utils/objectUtils'
import {
  isAncestorPath,
  normalizePath,
  pathPrefixes,
} from 'core/utils/schemaPaths'

import { JsonRecord } from './deriveColumns'

/**
 * `readOnly` is emitted only into the schemas that carry one, so the shared
 * generated type does not declare it. Widening here keeps that emission narrow
 * without every caller having to know.
 */
type FormFieldSchema = StaticFieldSchema & {
  readOnly?: boolean
  nested?: Array<FormFieldSchema>
}

/**
 * The edit form must offer only what the update endpoint accepts, so the omit
 * list is the whole rendered tree minus that endpoint's own fields. It walks
 * nested paths because an update body can accept `user.userName` while owning
 * `user.createdAt`, and returns the shallowest path per omitted subtree so an
 * object with nothing editable inside it disappears as one field.
 *
 * A field is omitted whatever the schema says when it is immutable on the
 * platform. Some update bodies are the resource type itself
 * (`RequestBodyCreditAgencyPut = CreditAgency`), which declares a record's own
 * identity and audit trail writable.
 */
const deriveEditOmitFields = ({
  record,
  responseSchema,
  updateSchema,
}: {
  record: JsonRecord
  responseSchema: Array<FormFieldSchema>
  updateSchema: Array<FormFieldSchema>
}): Array<string> => {
  const editable = new Set(
    collectWritablePaths(updateSchema)
      .filter((path) => !isImmutableField(path))
      .map(normalizePath),
  )

  const acceptsSomething = (path: string): boolean => {
    const normalized = normalizePath(path)
    if (editable.has(normalized)) return true
    for (const candidate of editable) {
      if (isAncestorPath(normalized, candidate)) return true
    }
    return false
  }

  const rendered = [
    ...new Set([
      ...collectSchemaPaths(responseSchema),
      ...collectRecordPaths(record),
    ]),
  ].sort(byDepthThenName)

  const omitted: Array<string> = []

  for (const path of rendered) {
    if (omitted.some((ancestor) => isAncestorPath(ancestor, path))) continue
    if (!acceptsSomething(path)) omitted.push(path)
  }

  return omitted.sort()
}

const collectSchemaPaths = (
  schema: Array<FormFieldSchema>,
  parentPath?: string,
): Array<string> =>
  schema.flatMap((field) => {
    const path = pathOf(field, parentPath)
    return [path, ...collectSchemaPaths(field.nested ?? [], path)]
  })

/**
 * The leaves an update body can actually carry a value for, dropping whatever
 * the spec marks `readOnly`. That flag is the API's own statement and is
 * resolved per definition, so it reaches nested fields no name-based rule
 * could judge: the spec marks `user.createdAt` read-only and `user.userName`
 * writable.
 *
 * Leaves only, so a container counts as editable when something inside it is.
 * An object whose every field is read-only accepts nothing, and the caller
 * collapses it to one omitted path rather than a shell of omitted children.
 */
const collectWritablePaths = (
  schema: Array<FormFieldSchema>,
  parentPath?: string,
): Array<string> =>
  schema.flatMap((field) => {
    if (field.readOnly === true) return []

    const path = pathOf(field, parentPath)
    const nested = field.nested ?? []

    return nested.length === 0 ? [path] : collectWritablePaths(nested, path)
  })

const pathOf = (field: FormFieldSchema, parentPath?: string): string =>
  field.fullPath ??
  (parentPath === undefined ? field.key : `${parentPath}.${field.key}`)

/** Array bookkeeping that `flattenObject` adds, which no field ever matches. */
const isLengthKey = (key: string) => key.endsWith('._length')

const collectRecordPaths = (record: JsonRecord): Array<string> =>
  Object.keys(flattenObject(record))
    .filter((key) => !isLengthKey(key))
    .flatMap(pathPrefixes)

const byDepthThenName = (a: string, b: string) =>
  pathPrefixes(a).length - pathPrefixes(b).length || a.localeCompare(b)

export default deriveEditOmitFields
